/**
 * Life Events page — dual-mode.
 *
 * For employees:    submit a new qualifying life event with
 *                   documentation upload (URL for now).
 * For admins:       list pending + reviewed life events with
 *                   approve / deny actions. Approval automatically
 *                   spawns a 30-day special enrollment period.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  ShieldAlert,
} from "lucide-react";

import { pageTransition } from "@/lib/utils";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import {
  getMyEnrollmentContext,
  listLifeEventsAdmin,
  reportLifeEvent,
  reviewLifeEvent,
  type LifeEvent,
  type LifeEventType,
} from "@/api/benefits";

const EVENT_LABELS: Record<LifeEventType, string> = {
  marriage: "Marriage",
  divorce: "Divorce / legal separation",
  birth: "Birth of child",
  adoption: "Adoption of child",
  death: "Death of dependent",
  loss_of_coverage: "Loss of other coverage",
  dependent_aging_out: "Dependent aging out (26)",
};

const STATUS_TONE: Record<string, "warning" | "success" | "default"> = {
  pending: "warning",
  approved: "success",
  denied: "default",
};

export function LifeEventsPage() {
  const { user } = useAuth();
  const isAdmin =
    user?.role === "HR_ADMIN" || user?.role === "SUPER_ADMIN";
  return isAdmin ? <AdminView /> : <EmployeeView />;
}


// ── Employee surface ───────────────────────────────────────────────


function EmployeeView() {
  const { addToast } = useToast();
  const [events, setEvents] = useState<LifeEvent[] | null>(null);
  const [open, setOpen] = useState(false);

  const reload = async () => {
    try {
      const ctx = await getMyEnrollmentContext();
      setEvents(ctx.life_events);
    } catch {
      setEvents([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Life events"
        subtitle="Report a qualifying life event to open a 30-day special enrollment window"
      />

      <Card>
        <CardHeader>
          <CardTitle>Your reports</CardTitle>
          <Button size="sm" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" />
            Report a life event
          </Button>
        </CardHeader>

        {!events ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : events.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            <Clock className="h-7 w-7 mx-auto mb-2 opacity-40" />
            You haven&rsquo;t reported any life events.
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {events.map((e) => (
              <li
                key={e.id}
                className="py-3 flex items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-text-primary">
                      {EVENT_LABELS[e.event_type]}
                    </span>
                    <Badge variant={STATUS_TONE[e.status] || "default"}>
                      {e.status}
                    </Badge>
                    <span className="text-xs text-text-muted">
                      Event date: {e.event_date}
                    </span>
                  </div>
                  {e.enrollment_window_end && e.status === "approved" && (
                    <p className="text-xs text-success mt-0.5">
                      Special enrollment window open until{" "}
                      {e.enrollment_window_end}.{" "}
                      <Link
                        to="/benefits/enrollment"
                        className="hover:underline"
                      >
                        Make elections →
                      </Link>
                    </p>
                  )}
                  {e.review_note && (
                    <p className="text-xs text-text-muted mt-0.5">
                      Note: {e.review_note}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ReportModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onSaved={async () => {
          setOpen(false);
          await reload();
          addToast("success", "Life event submitted for HR review");
        }}
      />
    </motion.div>
  );
}


function ReportModal({
  isOpen,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { addToast } = useToast();
  const [type, setType] = useState<LifeEventType>("marriage");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [docUrl, setDocUrl] = useState("");
  const [docName, setDocName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setType("marriage");
    setDate(new Date().toISOString().slice(0, 10));
    setDocUrl("");
    setDocName("");
    setNotes("");
  }, [isOpen]);

  const submit = async () => {
    setSaving(true);
    try {
      await reportLifeEvent({
        event_type: type,
        event_date: date,
        documentation_url: docUrl.trim() || undefined,
        documentation_name: docName.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onSaved();
    } catch {
      addToast("error", "Couldn't submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Report a life event">
      <div className="space-y-3">
        <Select
          label="Event type"
          value={type}
          onChange={(e) => setType(e.target.value as LifeEventType)}
          options={(Object.keys(EVENT_LABELS) as LifeEventType[]).map(
            (k) => ({ value: k, label: EVENT_LABELS[k] }),
          )}
        />
        <Input
          label="Event date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <Input
          label="Documentation file name (optional)"
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          placeholder="marriage-certificate.pdf"
        />
        <Input
          label="Documentation URL (optional)"
          value={docUrl}
          onChange={(e) => setDocUrl(e.target.value)}
          placeholder="https://..."
        />
        <div>
          <label className="block text-xs font-medium text-text-muted mb-1">
            Notes (optional)
          </label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} isLoading={saving}>
            Submit for HR review
          </Button>
        </div>
      </div>
    </Modal>
  );
}


// ── Admin surface ──────────────────────────────────────────────────


function AdminView() {
  const { addToast } = useToast();
  const [events, setEvents] = useState<LifeEvent[] | null>(null);
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = async () => {
    try {
      const data = await listLifeEventsAdmin(
        filter === "pending" ? "pending" : undefined,
      );
      setEvents(data);
    } catch {
      setEvents([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handle = async (e: LifeEvent, approve: boolean) => {
    setBusyId(e.id);
    try {
      const note = approve
        ? undefined
        : prompt("Reason for denial (optional):") || undefined;
      await reviewLifeEvent(e.id, approve, note);
      addToast(
        "success",
        approve
          ? "Approved — 30-day enrollment window opened"
          : "Marked as denied",
      );
      reload();
    } catch {
      addToast("error", "Couldn't update");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Life events"
        subtitle="Review qualifying life events. Approval opens a 30-day special enrollment window for the employee."
      />

      <Card>
        <CardHeader>
          <CardTitle>Reports</CardTitle>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setFilter("pending")}
              className={`px-3 py-1 text-xs rounded-full border ${
                filter === "pending"
                  ? "bg-accent text-white border-accent"
                  : "border-border text-text-muted hover:bg-surface-hover"
              }`}
            >
              Pending
            </button>
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`px-3 py-1 text-xs rounded-full border ${
                filter === "all"
                  ? "bg-accent text-white border-accent"
                  : "border-border text-text-muted hover:bg-surface-hover"
              }`}
            >
              All
            </button>
          </div>
        </CardHeader>

        {!events ? (
          <div className="py-10 text-center text-text-muted">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : events.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            <CheckCircle2 className="h-7 w-7 mx-auto mb-2 text-success" />
            Nothing to review.
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {events.map((e) => (
              <li
                key={e.id}
                className="py-3 flex items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-text-primary truncate">
                      {e.employee_name || "Employee"}
                    </span>
                    <Badge variant={STATUS_TONE[e.status] || "default"}>
                      {e.status}
                    </Badge>
                    <span className="text-sm text-text-primary">
                      {EVENT_LABELS[e.event_type]}
                    </span>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">
                    Event date {e.event_date}
                    {e.documentation_url && (
                      <>
                        {" · "}
                        <a
                          href={e.documentation_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-accent hover:underline"
                        >
                          {e.documentation_name || "Documentation"}
                        </a>
                      </>
                    )}
                  </p>
                  {e.notes && (
                    <p className="text-xs text-text-muted mt-1">{e.notes}</p>
                  )}
                </div>
                {e.status === "pending" && (
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handle(e, true)}
                      disabled={busyId === e.id}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handle(e, false)}
                      disabled={busyId === e.id}
                    >
                      <ShieldAlert className="h-3.5 w-3.5 text-danger" />
                      Deny
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </motion.div>
  );
}

export default LifeEventsPage;
